// ============================================================================
// TemplateApply — drop a saved workout template's exercises into this session
// in one tap. Wires to useTemplates (the picker) + useApplyTemplate (POST
// /sessions/{id}/apply-template). Shows how many sets were added on success.
// Reduced-motion-aware success flash. Hidden entirely when the trainer has no
// templates so it never nags.
// ============================================================================
import { useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Button, SelectField } from "../../components";
import { IconLayoutGrid } from "../../components/icons";
import { useTemplates } from "../../hooks/templates";
import { useApplyTemplate } from "../../hooks/sessions";

interface TemplateApplyProps {
  sessionId: number;
}

export function TemplateApply({ sessionId }: TemplateApplyProps) {
  const { t } = useTranslation("sessions");
  const reduce = useReducedMotion();
  const templatesQuery = useTemplates();
  const applyTemplate = useApplyTemplate();

  const [selected, setSelected] = useState("");
  const [added, setAdded] = useState<number | null>(null);
  const [error, setError] = useState<string | undefined>();

  const templates = templatesQuery.data ?? [];
  if (templatesQuery.isLoading || templates.length === 0) return null;

  const apply = () => {
    const id = Number(selected);
    if (!Number.isFinite(id) || id <= 0) return;
    setError(undefined);
    setAdded(null);
    applyTemplate.mutate(
      { sessionId, templateId: id },
      {
        onSuccess: (res) => {
          setAdded(res.created);
          setSelected("");
        },
        onError: () => setError(t("template.error")),
      },
    );
  };

  return (
    <div className="rounded-lg bg-violet-soft p-4 ring-1 ring-inset ring-ember/15">
      <div className="flex items-start gap-3">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-grad-violet text-white shadow-ember">
          <IconLayoutGrid width={18} height={18} />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="font-display text-sm font-bold text-body">
            {t("template.title")}
          </h3>
          <p className="mt-0.5 text-xs text-body-soft">{t("template.blurb")}</p>
        </div>
      </div>

      <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-end">
        <div className="flex-1">
          <SelectField
            label={t("template.pick")}
            hideLabel
            placeholder={t("template.pick")}
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            options={templates.map((tpl) => ({
              label: tpl.name?.trim() || `#${tpl.id}`,
              value: String(tpl.id),
            }))}
          />
        </div>
        <Button
          variant="secondary"
          onClick={apply}
          disabled={!selected || applyTemplate.isPending}
        >
          {applyTemplate.isPending ? t("template.applying") : t("template.apply")}
        </Button>
      </div>

      <AnimatePresence>
        {added != null && (
          <motion.p
            initial={reduce ? false : { opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="mt-2 text-sm font-bold text-lime-600"
            role="status"
          >
            {t("template.applied", { count: added })}
          </motion.p>
        )}
      </AnimatePresence>
      {error && (
        <p className="mt-2 text-sm font-medium text-danger" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
